import React, { useState, useEffect } from 'react';
import { Card, message, Spin, Form, Button, Row, Col, DatePicker, InputNumber, Tabs, Descriptions, Alert, Pagination, Modal, Popconfirm } from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { PlusOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';

const SportsTestComponent = ({ memberId, memberInfo, onMemberChange, phase }) => {
  // 设置 dayjs 为中文
  dayjs.locale('zh-cn');
  const { apiClient } = useAuth();
  
  // 体育测试相关状态
  const [testList, setTestList] = useState([]);
  const [testForm] = Form.useForm();
  const [testLoading, setTestLoading] = useState(false);
  const [testActiveTab, setTestActiveTab] = useState('1');
  const [isTestEditMode, setIsTestEditMode] = useState(false);
  // 分页状态
  const [testPagination, setTestPagination] = useState({ current: 1, pageSize: 1, total: 0 });
  const [testSearchParams, setTestSearchParams] = useState({ searchDate: null });
  
  // 当前选中的会员
  const [selectedMember, setSelectedMember] = useState(null);

  // 初始化会员信息
  useEffect(() => {
    if (memberId && memberInfo) {
      setSelectedMember({
        ...memberInfo,
        id: memberId
      });
      // 自动加载该会员的体育测试历史
      openTestModal({
        ...memberInfo,
        id: memberId
      }, 1, null, phase);
    }
  }, [memberId, memberInfo, phase]);

  // 打开体育测试详情
  const openTestModal = async (member, pageNum = 1, searchDate = null, phaseParam = null) => {
    setSelectedMember(member);
    setTestList([]);
    testForm.resetFields();
    testForm.setFieldsValue({
      babyName: member.babyName,
      trainDate: dayjs(),
    });
    // 重置tab到第一个
    setTestActiveTab('1');
    // 重置为新增模式
    setIsTestEditMode(false);

    try {
      setTestLoading(true);
      const params = {
        babyId: member.id,
        pageSize: 1, // 每页1条记录
        pageNo: pageNum,
      };
      
      // 如果有指定日期，则添加日期筛选
      if (searchDate) {
        params.searchDate = searchDate.format('YYYY-MM-DD');
      }
      
      // 如果有指定阶段，则添加阶段筛选
      if (phaseParam !== null && phaseParam !== undefined) {
        params.phase = phaseParam;
      }

      const response = await apiClient.post('/sports-server-prod/taller.api.v1/train/pageList', params);

      if (response.data && response.data.code === 200) {
        setTestList(response.data.data?.list || []);
        setTestPagination({
          current: pageNum,
          pageSize: 1,
          total: response.data.data?.totalRecord || 0
        });
        setTestSearchParams({ searchDate });
      } else {
        message.error(response.data?.msg || '获取体育测试历史失败');
      }
    } catch (error) {
      console.error('获取体育测试历史失败:', error);
      message.error('获取体育测试历史失败');
    } finally {
      setTestLoading(false);
    }
  };

  // 处理体育测试分页变化
  const handleTestPageChange = (page) => {
    openTestModal(
      selectedMember, 
      page, 
      testSearchParams.searchDate,
      phase
    );
  };

  // 处理日期筛选（测试历史tab的日期选择）
  const handleTestHistoryDateChange = (date) => {
    // 重新加载列表
    openTestModal(
      selectedMember, 
      1, 
      date,
      phase
    );
  };

  // 处理日期筛选（编辑tab的日期选择）
  const handleTestDateChange = (date) => {
    setTestSearchParams({ searchDate: date });
  };

  // 打开修改体育测试的标签页
  const openEditTestTab = (test) => {
    // 设置表单值
    testForm.setFieldsValue({
      id: test.id,
      babyName: selectedMember.babyName,
      trainDate: dayjs(test.trainDate),
      trainPoint1: test.trainPoint1,
      trainPoint2: test.trainPoint2,
      trainPoint3: test.trainPoint3,
      trainPoint4: test.trainPoint4,
      trainPoint5: test.trainPoint5,
      trainPoint6: test.trainPoint6,
    });
    
    // 更新日期搜索参数
    setTestSearchParams({ searchDate: dayjs(test.trainDate) });
    
    // 设置为编辑模式
    setIsTestEditMode(true);
    
    // 切换到第二个tab
    setTestActiveTab('2');
  };

  // 切换到新增模式
  const switchToNewTestMode = () => {
    // 重置表单
    testForm.resetFields();
    testForm.setFieldsValue({
      babyName: selectedMember.babyName,
      trainDate: dayjs(),
    });
    // 清空日期搜索参数
    setTestSearchParams({ searchDate: null });
    // 设置为新增模式
    setIsTestEditMode(false);
  };

  // 保存体育测试
  const handleSaveTest = async () => {
    try {
      const values = await testForm.validateFields();

      if (!selectedMember) {
        message.error('未找到当前会员信息');
        return;
      }

      const userStr = localStorage.getItem('user') || '{}';
      const user = JSON.parse(userStr);
      const currentDate = dayjs().format('YYYYMMDD');

      const payload = {
        itemRecords: [{
          id: values.id || undefined,
          babyId: selectedMember.id,
          trainPoint1: values.trainPoint1,
          trainPoint2: values.trainPoint2,
          trainPoint3: values.trainPoint3,
          trainPoint4: values.trainPoint4,
          trainPoint5: values.trainPoint5,
          trainPoint6: values.trainPoint6,
          trainDate: values.trainDate.format('YYYY-MM-DD'),
          phase: phase, // 添加阶段参数
          createUser: user.name || '',
          createDate: currentDate,
          updateUser: user.name || '',
          updateDate: currentDate,
          status: '1'
        }]
      };

      setTestLoading(true);
      const resp = await apiClient.post('/sports-server-prod/taller.api.v1/train/add', payload);
      if (resp.data && resp.data.code === 200) {
        message.success('体育测试保存成功');
        // 重置tab到第一个
        setTestActiveTab('1');
        // 重置为新增模式
        setIsTestEditMode(false);
        // 重新获取体育测试历史
        await openTestModal(selectedMember, 1, null, phase);
      } else {
        message.error(resp.data?.msg || '体育测试保存失败');
      }
    } catch (error) {
      if (error.errorFields) {
        return;
      }
      console.error('保存体育测试失败:', error);
      message.error('保存体育测试失败');
    } finally {
      setTestLoading(false);
    }
  };

  // 删除体育测试
  const handleDeleteTest = async (testId) => {
    try {
      setTestLoading(true);
      const response = await apiClient.post(`/sports-server-prod/taller.api.v1/train/delete?id=${testId}`);
      
      if (response.data && response.data.code === 200) {
        message.success('删除成功');
        // 重新加载数据
        await openTestModal(selectedMember, 1, testSearchParams.searchDate, phase);
      } else {
        message.error(response.data?.msg || '删除失败');
      }
    } catch (error) {
      console.error('删除体育测试失败:', error);
      message.error('删除失败');
    } finally {
      setTestLoading(false);
    }
  };

  // 当会员信息发生变化时的回调
  const handleMemberInfoChange = (newMemberInfo) => {
    if (onMemberChange) {
      onMemberChange(newMemberInfo);
    }
  };

  return (
    <div className="sports-test-component">
      <Card 
        title={phase !== undefined && phase !== null ? `第 ${phase} 阶段体育测试` : "阶段体育测试"} 
        extra={
          <Button 
            icon={<SyncOutlined />} 
            onClick={() => openTestModal(selectedMember, 1, testSearchParams.searchDate, phase)}
            loading={testLoading}
          >
            刷新
          </Button>
        }
        style={{ margin: '16px' }}
      >
        {selectedMember ? (
          <div>
            <div style={{ marginBottom: 16 }}>
              <span style={{ marginRight: 16 }}>当前会员: <strong>{selectedMember.babyName}</strong></span>
              <span>会员ID: {selectedMember.id}</span>
            </div>

            <Tabs 
              defaultActiveKey="1" 
              activeKey={testActiveTab} 
              onChange={setTestActiveTab}
            >
              <Tabs.TabPane tab="测试历史" key="1">
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                  <DatePicker
                    placeholder="选择日期"
                    onChange={handleTestHistoryDateChange}
                    value={testSearchParams.searchDate}
                    allowClear
                  />
                </div>
                
                <Spin spinning={testLoading}>
                  {testList.length > 0 ? (
                    testList.map((item, index) => (
                      <Card 
                        key={item.id || index} 
                        style={{ marginBottom: 16 }}
                        extra={
                          <div>
                            <Button 
                              type="primary" 
                              size="small" 
                              icon={<EditOutlined />}
                              onClick={() => openEditTestTab(item)}
                              style={{ marginRight: 8 }}
                            >
                              修改
                            </Button>
                            <Popconfirm
                              title="确定要删除这条测试记录吗？"
                              onConfirm={() => handleDeleteTest(item.id)}
                              okText="确定"
                              cancelText="取消"
                            >
                              <Button 
                                type="danger" 
                                size="small" 
                                icon={<DeleteOutlined />}
                                danger
                              >
                                删除
                              </Button>
                            </Popconfirm>
                          </div>
                        }
                      >
                        <Descriptions title={`测评日期: ${item.trainDate}`} bordered column={2}>
                          <Descriptions.Item label="坐位体前屈">{item.trainPoint1 !== null && item.trainPoint1 !== undefined ? `${item.trainPoint1} cm` : '未测试'}</Descriptions.Item>
                          <Descriptions.Item label="摸高">{item.trainPoint2 !== null && item.trainPoint2 !== undefined ? `${item.trainPoint2} cm` : '未测试'}</Descriptions.Item>
                          <Descriptions.Item label="一分钟跳绳">{item.trainPoint3 !== null && item.trainPoint3 !== undefined ? `${item.trainPoint3} 次` : '未测试'}</Descriptions.Item>
                          <Descriptions.Item label="4*10m跑">{item.trainPoint4 !== null && item.trainPoint4 !== undefined ? `${item.trainPoint4} 秒` : '未测试'}</Descriptions.Item>
                          <Descriptions.Item label="立定跳远">{item.trainPoint5 !== null && item.trainPoint5 !== undefined ? `${item.trainPoint5} cm` : '未测试'}</Descriptions.Item>
                          <Descriptions.Item label="仰卧起坐">{item.trainPoint6 !== null && item.trainPoint6 !== undefined ? `${item.trainPoint6} 次` : '未测试'}</Descriptions.Item>
                        </Descriptions>
                      </Card>
                    ))
                  ) : (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                      暂无体育测试历史
                    </div>
                  )}
                  
                  {testPagination.total > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
                      <span>共 {testPagination.total} 条记录，第 {testPagination.current} / {Math.ceil(testPagination.total / testPagination.pageSize)} 页</span>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Button
                          size="small"
                          disabled={testPagination.current <= 1}
                          onClick={() => handleTestPageChange(testPagination.current - 1)}
                        >
                          上一页
                        </Button>
                        <Button
                          size="small"
                          disabled={testPagination.current >= Math.ceil(testPagination.total / testPagination.pageSize)}
                          onClick={() => handleTestPageChange(testPagination.current + 1)}
                        >
                          下一页
                        </Button>
                      </div>
                    </div>
                  )}
                </Spin>
              </Tabs.TabPane>

              <Tabs.TabPane tab="新增/编辑测试" key="2">
                {isTestEditMode && (
                  <div style={{ marginBottom: 16, textAlign: 'right' }}>
                    <Button 
                      type="default" 
                      onClick={switchToNewTestMode}
                      icon={<PlusOutlined />}
                    >
                      切换到新增模式
                    </Button>
                  </div>
                )}
                <Form form={testForm} layout="vertical">
                  {/* 隐藏的id字段，用于修改模式 */}
                  <Form.Item name="id" hidden>
                    <InputNumber />
                  </Form.Item>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item label="会员姓名" name="babyName">
                        <InputNumber disabled style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        label="测评日期"
                        name="trainDate"
                        rules={[{ required: true, message: '请选择测评日期' }]}
                      >
                        <DatePicker 
                          style={{ width: '100%' }} 
                          format="YYYY-MM-DD" 
                          onChange={handleTestDateChange}
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  <div style={{ 
                    backgroundColor: '#f0f2f5', 
                    padding: '16px', 
                    borderRadius: '4px', 
                    marginBottom: '16px' 
                  }}>
                    <h3 style={{ marginTop: 0, marginBottom: '16px' }}>身体素质测试项目</h3>
                    
                    <Row gutter={16}>
                      <Col span={8}>
                        <Form.Item
                          label="坐位体前屈(cm)"
                          name="trainPoint1"
                        >
                          <InputNumber 
                            min={0} 
                            precision={1}
                            style={{ width: '100%' }} 
                            placeholder="请输入测试结果"
                            parser={(value) => value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1')}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          label="摸高(cm)"
                          name="trainPoint2"
                        >
                          <InputNumber 
                            min={0} 
                            precision={1}
                            style={{ width: '100%' }} 
                            placeholder="请输入测试结果"
                            parser={(value) => value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1')}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          label="一分钟跳绳(次)"
                          name="trainPoint3"
                        >
                          <InputNumber 
                            min={0} 
                            precision={0}
                            style={{ width: '100%' }} 
                            placeholder="请输入测试结果"
                            parser={(value) => value.replace(/[^\d]/g, '')}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={16}>
                      <Col span={8}>
                        <Form.Item
                          label="4*10m跑(秒)"
                          name="trainPoint4"
                        >
                          <InputNumber 
                            min={0} 
                            precision={2}
                            style={{ width: '100%' }} 
                            placeholder="请输入测试结果"
                            parser={(value) => value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1')}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          label="立定跳远(cm)"
                          name="trainPoint5"
                        >
                          <InputNumber 
                            min={0} 
                            precision={1}
                            style={{ width: '100%' }} 
                            placeholder="请输入测试结果"
                            parser={(value) => value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1')}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={8}>
                        <Form.Item
                          label="仰卧起坐(次)"
                          name="trainPoint6"
                        >
                          <InputNumber 
                            min={0} 
                            precision={0}
                            style={{ width: '100%' }} 
                            placeholder="请输入测试结果"
                            parser={(value) => value.replace(/[^\d]/g, '')}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </div>

                  <div style={{ textAlign: 'right', marginTop: 16 }}>
                    <Button onClick={() => {
                      testForm.resetFields();
                    }} style={{ marginRight: 8 }}>
                      重置
                    </Button>
                    <Button type="primary" loading={testLoading} onClick={handleSaveTest}>
                      保存
                    </Button>
                  </div>
                </Form>
              </Tabs.TabPane>
            </Tabs>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div>未提供会员信息</div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default SportsTestComponent;